import { IOnInit } from "angular";
import {
  AssetClassNames,
  AssetClassNameArray,
} from "@common/enums/AssetClassType";
import { CModelRepo } from "@services/cmodel.repo";
import { FeesRepo } from "@services/fees.repo";
import { PortfolioRepo } from "@services/portfolio.repo";
import { DropdownOptionsService } from "@services/clients/dropdownOptions.service";
import { ClientCRMRepo } from "@services/clients/clientCRM.repo";
import * as moment from "moment";
import { extractErrorMessage } from "@common/utils/errorUtils";

class CreatePortfolioFeeExclusionDialogController implements IOnInit {
  public portfolioId: number;
  public portfolioName: string;
  public portfolios: any[] = [];
  public selectedPortfolio: any = null;
  public assetClassNames = AssetClassNameArray;
  public selectedAssetClass;
  public portfolioFeeExclusionForm: ng.IFormController;
  public securitiesList;
  public isLoading: boolean = false;
  public isLoadingSecurities: boolean = false;
  public excludeMarketValue: boolean = false;
  public selectedSecurity;
  public marketValue: number;
  public isCashExclusion: boolean = false;
  public cashAmount: number;
  public cashCurrency: string;
  public dropdownOptions: any = {};
  public currenciesWithCodes: any[] = []; // Full currency list with codes
  public toDate: string;
  public fromDate: string;
  public searchText: any = "";
  public hasPortfolioInSecurity: boolean = true;

  /** @ngInject */
  constructor(
    private $mdDialog: angular.material.IDialogService,
    private cModelRepo: CModelRepo,
    private feesRepo: FeesRepo,
    private notifyjs,
    private portfolioRepo: PortfolioRepo,
    private dropdownOptionsService: DropdownOptionsService,
    private clientCRMRepo: ClientCRMRepo
  ) {}

  $onInit() {
    // Auto-select when only one portfolio is available
    if (this.portfolios && this.portfolios.length === 1) {
      this.selectedPortfolio = this.portfolios[0];
      this.onPortfolioChange();
    }

    this.dropdownOptionsService
      .getAllOptions()
      .then((options) => {
        this.dropdownOptions = options;
      })
      .catch((error) => {
        // Non-blocking: log and continue with empty options
        console.warn(
          "Failed to load dropdown options for portfolio fee exclusion modal:",
          error
        );
      });

    // Load full currency list with codes for ID-to-code conversion
    this.clientCRMRepo
      .getCurrencies()
      .then((response: any) => {
        this.currenciesWithCodes = response.data || [];
      })
      .catch((error) => {
        console.warn(
          "Failed to load currencies with codes for portfolio fee exclusion modal:",
          error
        );
      });
  }

  getSecurities = () => {
    if (this.selectedAssetClass) {
      this.isCashExclusion = this.selectedAssetClass === AssetClassNames.CASH;

      // For cash exclusions we don't need to load securities
      if (this.isCashExclusion) {
        this.securitiesList = [];
        this.selectedSecurity = null;
        this.isLoadingSecurities = false;
        this.autopopulateCashCurrency();
        return;
      }

      this.isLoadingSecurities = true;
      this.securitiesList = [];
      if (this.hasPortfolioInSecurity) {
        this.portfolioRepo
        .getPortfolioSecurities(this.portfolioId, this.selectedAssetClass)
        .then((res) => {
          this.isLoadingSecurities = false;
          this.securitiesList = res.data;
        })
        .catch((err) => {
          this.isLoadingSecurities = false;
        });
      } else {
        this.cModelRepo
        .getSecurityItemsList(this.selectedAssetClass, null, false)
        .then((res) => {
          this.isLoadingSecurities = false;
          this.securitiesList = res;
        })
        .catch((err) => {
          this.isLoadingSecurities = false;
        });
      }
    }
  };

  onPortfolioChange = () => {
    if (this.selectedPortfolio) {
      this.portfolioId = this.selectedPortfolio.portfolioId;
      this.portfolioName = this.selectedPortfolio.portfolioName;
      // Reset security selection when portfolio changes
      this.selectedSecurity = null;
      this.securitiesList = [];
      if (this.selectedAssetClass) {
        this.getSecurities();
      }
      if (this.isCashExclusion) {
        this.autopopulateCashCurrency();
      }
    }
  };

  private autopopulateCashCurrency = () => {
    const defaultCurrency = this.selectedPortfolio && this.selectedPortfolio.defaultCurrency;
    this.cashCurrency = defaultCurrency || "MUR";
  };

  cancel = () => {
    this.$mdDialog.cancel();
  };

  save = () => {
    const actualFromDate = moment.utc(this.fromDate).toDate();

    const actualtoDate =
      this.toDate
        ? moment.utc(this.toDate).toDate()
        : null;

    let portfolioFeeExclusion: any = {
      portfolioId: this.portfolioId,
      fromDate: actualFromDate,
      toDate: actualtoDate,
    };

    if (this.isCashExclusion) {
      portfolioFeeExclusion.isCashExclusion = true;
      portfolioFeeExclusion.cashAmount = this.cashAmount;
      portfolioFeeExclusion.cashCurrency = this.cashCurrency;
    } else {
      portfolioFeeExclusion.securityId = this.selectedSecurity.id;
      portfolioFeeExclusion.excludeMarketValue = this.excludeMarketValue;
      portfolioFeeExclusion.marketValue = this.excludeMarketValue ? 0 : this.marketValue;
    }

    this.isLoading = true;
    this.feesRepo
      .savePortfolioFeeExclusion(portfolioFeeExclusion)
      .then((res) => {
        this.$mdDialog.hide(portfolioFeeExclusion.portfolioId);
      })
      .catch((err) => {
        this.notifyjs("Failed to save fee exclusion : " + extractErrorMessage(err));
      })
      .finally(() => {
        this.isLoading = false;
      });
  };

  clearSearchText = () => {
    this.searchText = "";
  };

  public getCashCurrencyLabel = (): string => {
    if (!this.cashCurrency || !this.currenciesWithCodes || !this.currenciesWithCodes.length) {
      return "";
    }
    const currency = this.currenciesWithCodes.find((c: any) => c.code === this.cashCurrency);
    return currency ? currency.symbol : "";
  };
}

export const createPortfolioFeeExclusionDialog: angular.IComponentOptions = {
  template: require("./createPortfolioFeeExclusion.dialog.html"),
  controller: CreatePortfolioFeeExclusionDialogController,
  bindings: {
    portfolioId: "<?",
    portfolioName: "@?",
    portfolios: "<?",
  },
};



// WEBPACK FOOTER //
// ./src/app/fees/clientFeeManagement/createFeeExclusion/createPortfolioFeeExclusion.dialog.ts